import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, RotateCw, Home } from "lucide-react";

/**
 * PWANavBar
 *
 * When Cauldron is installed as a Progressive Web App ("Add to Home
 * Screen"), the browser UI — including the back/forward buttons — is
 * hidden. Users get stranded on whatever page they land on with no way to
 * navigate history.
 *
 * This bar appears ONLY when:
 *   • The app is launched in standalone display-mode (PWA), AND
 *   • The user is NOT on the absolute landing route
 *
 * It pins to the bottom-left of the viewport in a translucent floating
 * pill so it never blocks content but is always reachable with one thumb.
 *
 * The buttons drive `window.history.back()` / `forward()` (NOT React
 * Router's navigate(-1)) — they walk the actual browser history stack,
 * which is what users expect from a "back button". A Home shortcut routes
 * to "/" via React Router so it works even when history is empty.
 */
export default function PWANavBar() {
  const nav = useNavigate();
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    const check = () => {
      const isStandalone =
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true;
      // Hide inside the Capacitor native shell — it has its own native nav.
      const inNative = !!window.Capacitor?.isNativePlatform?.();
      setStandalone(isStandalone && !inNative);
    };
    check();
    const mq = window.matchMedia?.("(display-mode: standalone)");
    mq?.addEventListener?.("change", check);
    return () => mq?.removeEventListener?.("change", check);
  }, []);

  if (!standalone) return null;

  return (
    <div
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-1 rounded-full border border-amber-500/40 bg-background/90 backdrop-blur px-2 py-1.5 shadow-xl"
      data-testid="pwa-nav-bar"
      role="navigation"
      aria-label="PWA navigation"
    >
      <button
        onClick={() => window.history.back()}
        className="p-2 rounded-full hover:bg-amber-500/15 text-foreground/80 hover:text-amber-300 transition-colors"
        aria-label="Back"
        data-testid="pwa-nav-back"
      >
        <ChevronLeft size={18} strokeWidth={2} />
      </button>
      <button
        onClick={() => window.history.forward()}
        className="p-2 rounded-full hover:bg-amber-500/15 text-foreground/80 hover:text-amber-300 transition-colors"
        aria-label="Forward"
        data-testid="pwa-nav-forward"
      >
        <ChevronRight size={18} strokeWidth={2} />
      </button>
      <div className="w-px h-5 bg-amber-500/25 mx-0.5" />
      <button
        onClick={() => nav("/")}
        className="p-2 rounded-full hover:bg-amber-500/15 text-foreground/80 hover:text-amber-300 transition-colors"
        aria-label="Home"
        data-testid="pwa-nav-home"
      >
        <Home size={16} strokeWidth={2} />
      </button>
      <button
        onClick={() => window.location.reload()}
        className="p-2 rounded-full hover:bg-amber-500/15 text-foreground/80 hover:text-amber-300 transition-colors"
        aria-label="Reload"
        data-testid="pwa-nav-reload"
      >
        <RotateCw size={14} strokeWidth={2} />
      </button>
    </div>
  );
}
